import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/ducks';
import { format } from 'date-fns';

import DomainSettingService from 'services/domainSetting.service';
import SubsidiaryService from 'services/subsidiary.service';
import TicketServices from 'services/ticket.service';
import UploadService from 'services/upload.service';

import Form from 'components/Form';
import Text from 'components/Text';
import { Button } from 'components/Button';
import { Row, Column } from 'components/Layout/Grid';
import { CustomerData } from './CustomerData';
import { CommunicationChannel } from './CommunicationChannel';
import { ProtocolCategorySimple } from './ProtocolCategorySimple';
import { TechnicalReport } from './TechnicalReport';
import { BrandSimple } from './BrandSimple';
import BU from 'pages/OpenProtocol/BU';
import { AdditionalContact } from './AdditionalContact';
import { OrderData } from './OrderData';
import { DescriptionSimple } from './DescriptionSimple';
import { AttachmentSimple } from './AttachmentSimple';
import { TagSimple } from './TagSimple';
import { abrirProtocoloAction, eventName } from 'components/ApplicationInsights/eventNames';
import toast from 'components/Toast/dispatch';
import { useCustomEvent } from 'components/ApplicationInsights';

import { PhoneNumberParse } from 'utils/PhoneNumber';
import { EnumToArray } from 'utils/enumArray/enumHelpers';
import { SetMessageErrorNewProtocol } from 'utils/OpenProtocol';
import { ICategoriesReasons, IPipefyOption } from 'components/Select/interface';
import { phaseToEditProtocolSimple } from 'pages/OpenProtocol/interface';
import { FormSimpleProps, SimpleFormProps, SubsidiaryProps } from './interface';
import { EnumCategories, ILegalPersonType, ITicketAttendanceType, ManualSubsidiariesEnum } from 'models/Ticket';
import { IDomainSetting } from 'models/DomainSetting';

export const SimpleForm: React.FC<SimpleFormProps> = (props) => {
  const { stateLocation, setSuccess, setProtocolNumberCreated, ResetCompany } = props;

  const user = useSelector((state: RootState) => state.user);
  const customer = useSelector((state: RootState) => state.customer);
  const trackMenuClick = useCustomEvent('Button', eventName.PROTOCOLOS);

  const [optionalButtonsTypes, setOptionalButtonsTypes] = useState(Array<IDomainSetting>());
  const [communicationChannel, setCommunicationChannel] = useState<string | undefined>(stateLocation?.communicationChannel || '');
  const [customerName, setCustomerName] = useState(stateLocation?.customerName || '');
  const [documentNumber, setDocumentNumber] = useState(stateLocation?.documentNumber || '');
  const [protocolCategory, setProtocolCategory] = useState(stateLocation?.protocolCategory || '');
  const [ticketReason, setTicketReason] = useState(stateLocation?.ticketReason || '');
  const [protocolClassification, setProtocolClassification] = useState(stateLocation?.protocolClassification || '');
  const [brandSelected, setBrandSelected] = useState(stateLocation?.brands || '');
  const [nameBrandSelected, setNameBrandSelected] = useState<string>("");
  const [buSelected, setBuSelected] = useState(stateLocation?.businessUnits || '');
  const [manualDevolutionInvoiceNumber, setManualDevolutionInvoiceNumber] = useState(stateLocation?.manualDevolutionInvoiceNumber || '');
  const [contactName, setContactName] = useState(
    stateLocation?.contactName || ""
  );
  const [contactMailAddress, setContactMailAddress] = useState(
    stateLocation?.contactMailAddress || ""
  );
  const [contactPhoneNumber, setContactPhoneNumber] = useState(
    stateLocation?.contactPhoneNumber || ""
  );
  const [order, setOrder] = useState(stateLocation?.manualOrderNumber || '');
  const [invoice, setInvoice] = useState(stateLocation?.manualInvoiceNumber || '');
  const [description, setDescription] = useState(
    stateLocation?.description || ''
  );
  const [files, setFiles] = useState<Array<any>>(
    stateLocation?.attachments || []
  );

  const isEditing = !!stateLocation?.protocolNumber;

  const [protocolNumber] = useState(stateLocation?.protocolNumber || '');
  const [tags, setTags] = useState<string[]>(stateLocation?.tags || []);
  const [loading, setLoading] = useState(false);
  const [fail, setFail] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [allCategories, setAllCategories] = useState<Array<IPipefyOption>>([]);
  const [allReasons, setAllReasons] = useState<Array<IPipefyOption>>([]);
  const [allClassifications, setAllClassifications] = useState<Array<IPipefyOption>>([]);
  const [othersOccurrence, setOthersOccurrence] = useState(stateLocation?.occurrenceTypeDescription || '');
  const [occurrenceType, setOccurrenceType] = useState(stateLocation?.occurrenceType || '');
  const [sampleType, setSampleType] = useState<string>(stateLocation?.sampleType || '');
  const [technicalDescription, setTechnicalDescription] = useState(stateLocation?.description || '');
  const [occurrenceDate, setOccurrenceDate] = useState<string>(stateLocation?.occurrenceDate ?
    format(new Date(stateLocation?.occurrenceDate), 'yyyy-MM-dd')
    : '');
  const [uncontaminatedProductSampleType, setUncontaminatedProductSampleType] = useState<string>(
    isEditing && stateLocation?.availableSampleCollection?.toString() || '');
  const [remainingBalance, setRemainingBalance] = useState<boolean | undefined>(stateLocation?.balanceRemaining || false);
  const [qtdRemainingBalance, setQtdRemainingBalance] = useState<string | undefined>(stateLocation?.balanceRemainingQuantity || '');
  const [subsidiary, setAllSubsidiary] = useState<SubsidiaryProps[]>([]);

  const _domainSettingService = new DomainSettingService();
  const _ticketService = new TicketServices();
  const _uploadService = new UploadService();
  const _subsidiaryService = new SubsidiaryService();

  const getCommunicationChannel = async () => {
    const response = await _domainSettingService
      .get(undefined, undefined, undefined, {
        and: [
          { identifier: { eq: 'CommunicationChannelType' } },
          { enabled: { eq: true } },
        ],
      });

    const { data } = response;

    if (data) {
      let _optionalButtonTypes: Array<IDomainSetting> = data;
      _optionalButtonTypes = _optionalButtonTypes.filter((x: IDomainSetting) => x.key !== 'AUTO_ATTENDANCE');

      setOptionalButtonsTypes(_optionalButtonTypes);
    }
  };

  const getCategoriesReasons = async () => {
    const options: ICategoriesReasons = await _ticketService.getCategoriesReasons();

    const { data } = options.data;
    if (data) {
      if (isEditing) {
        setProtocolCategory(data.categories.find(x => x.pipefyLabel === stateLocation?.protocolCategory)?.value);
        setTicketReason(data.reasons.find(x => x.pipefyLabel === stateLocation?.ticketReason)?.value);
        setProtocolClassification(data.classifications.find(x => x.pipefyLabel === stateLocation?.protocolClassification)?.value);
      }

      setAllCategories(data.categories);
      setAllReasons(data.reasons);
      setAllClassifications(data.classifications);
    }
  };

  const getSubsidiaries = async () => {
    const manualSubsidiaries: number[] = [9997, 9998, 9999];
    const response = await _subsidiaryService.get(undefined, undefined, 'enterpriseName asc', {
      and: [{ subsidiaryCode: { in: manualSubsidiaries } }]
    });
    const { data } = response;
    const subsidiaries: SubsidiaryProps[] = data;

    setAllSubsidiary(subsidiaries);
  };

  useEffect(() => {
    getSubsidiaries();
    getCommunicationChannel();
    getCategoriesReasons();
  }, []);

  const onCheckSubmit = () => {
    setLoading(true);
    setErrorMessage('');
    let data: FormSimpleProps = {};

    if (isEditing) {
      data = { ...stateLocation, ...data };

      const status = stateLocation?.phaseStatus?.toLowerCase();
      const allowEdit = phaseAllowedForEditing(status);

      if (allowEdit) {
        onSubmit(data);
        return;
      }

      if (!allowEdit) {
        setErrorMessage(`Desculpe, mas você não possui permissão para editar protocolos na etapa de (${status}). Caso precise de acesso, por favor, abra um chamado no TopDesk.`);
        setLoading(false);
        return;
      }
    }

    onSubmit(data);
  };

  const phaseAllowedForEditing = (status: string) => {
    const existPhase = phaseToEditProtocolSimple.includes(status);

    if (existPhase)
      return conditionToEditPhase(status);

    return false;

  };

  const conditionToEditPhase = (status: string) => {
    if ((status === 'avaliação comercial' &&
      (!user?.profileType?.includes('INTERNAL_COMMERCIAL'))))
      return false;

    return true;
  };

  const onSubmit = async (data: FormSimpleProps) => {

    if (data.customerId === undefined) {
      data.customerId =
        customer?.id ||
        user?.selectedCustomerId ||
        user?.customerId;
    }

    if (user?.userType === 'customer') {
      data.communicationChannel = data.communicationChannel || "PORTAL_CC";
    } else data.communicationChannel = communicationChannel || undefined;

    data.userTypeProtocol = 3;
    data.legalPerson = documentNumber?.replace(/[^0-9]/g, '').length === 11 ? ILegalPersonType.NATURAL_PERSON : ILegalPersonType.JURIDICAL_PERSON || undefined;
    data.documentNumber = documentNumber?.replace(/[^0-9]/g, '') || undefined;
    data.protocolNumber = protocolNumber || undefined;
    data.contactName = contactName;
    data.contactMailAddress = contactMailAddress;
    data.contactPhoneNumber = contactPhoneNumber
      ? PhoneNumberParse(contactPhoneNumber)
      : undefined;

    data.protocolCategory = protocolCategory
      ? allCategories.find(x => x.value === protocolCategory)?.pipefyLabel
      : undefined;
    data.ticketReason = ticketReason
      ? allReasons.find(x => x.value === ticketReason)?.pipefyLabel
      : undefined;
    data.protocolClassification = protocolClassification
      ? allClassifications.find(x => x.value === protocolClassification)?.pipefyLabel
      : undefined;

    data.tags = tags;
    data.brands = brandSelected || undefined;
    data.businessUnits = buSelected || '';
    data.manualOrderNumber = order;
    data.manualInvoiceNumber = invoice;
    data.description = description;
    data.customerName = customerName;
    data.manualDevolutionInvoiceNumber = manualDevolutionInvoiceNumber;

    if (protocolCategory === EnumCategories.RECLAMACAO_TECNICA) {
      data.occurrenceDate = occurrenceDate;
      data.balanceRemaining = remainingBalance;
      data.balanceRemainingQuantity = qtdRemainingBalance;
      data.occurrenceType = occurrenceType;
      data.occurrenceTypeDescription = othersOccurrence;
      data.sampleType = sampleType ? sampleType : '';
      data.availableSampleCollection = uncontaminatedProductSampleType ? JSON.parse(uncontaminatedProductSampleType) : false;
      data.description = technicalDescription;
    }

    data.subsidiaryId = validateAndReturnSubsidiaryId();

    data.attendenceType = EnumToArray(ITicketAttendanceType).find(
      (x) => x.value === ITicketAttendanceType.SERVED_BY_ATTENDENT
    )?.key;

    const uploadedFiles = files.map((file) =>
      file.uri !== undefined ? file : _uploadService.upload(file)
    );

    data.attachments = await Promise.all(uploadedFiles)
      .then((resultFiles: any) =>
        resultFiles.map((file: any) => ({
          id: file.id,
          name: file.name,
          fileUrl: file.uri || file.fileUrl,
        }))
      )
      .catch(() => {
        setLoading(false);
        toast.error('Falha ao anexar arquivo(s).');
        return false;
      });

    if (isEditing) {
      const response = await _ticketService.update(data);

      if (response.status === 200) {
        setSuccess(true);
        ResetCompany();
      } else {
        if (response.data.code) {
          setErrorMessage(response.data.data);
        } else SetMessageErrorNewProtocol(setErrorMessage, response.data.data);
        setLoading(false);
      }
    } else {
      const response = await _ticketService.create(data);

      if (response.status === 200) {
        setSuccess(true);
        setProtocolNumberCreated(response.data.data.protocolNumber);
        ResetCompany();
        trackMenuClick({ item: abrirProtocoloAction.REGISTRARPROTOCOLOSIMPLES });
      } else {
        if (response.data.code) {
          setErrorMessage(response.data.data);
        } else SetMessageErrorNewProtocol(setErrorMessage, response.data.data);
        setLoading(false);
      }
    }
  };

  const validateAndReturnSubsidiaryId = () => {
    switch (nameBrandSelected) {
      case "Mafra Especialidades":
        return subsidiary.find((sub: SubsidiaryProps) => sub?.subsidiaryCode === ManualSubsidiariesEnum.PFS)?.id;

      case "Neve":
        return subsidiary.find((sub: SubsidiaryProps) => sub?.subsidiaryCode === ManualSubsidiariesEnum.NEVE)?.id;

      default:
        return subsidiary.find((sub: SubsidiaryProps) => sub?.subsidiaryCode === ManualSubsidiariesEnum.FLEXICOTTON_FW_DAVISO)?.id;
    }
  };

  return (
    <Form
      id="open_protocol_form_simple"
      data-testid="open_protocol_form_simple"
      onValidate={onCheckSubmit}
      customFunction={(result: boolean) => setFail(result)}
    >
      <Column
        className="bg-white flex-1 px-3 pb-3 pt-2"
      >
        <CustomerData
          customerName={customerName}
          setCustomerName={setCustomerName}
          documentNumber={documentNumber}
          setDocumentNumber={setDocumentNumber}
          isEditing={isEditing}
        />

        <CommunicationChannel
          optionalButtonsTypes={optionalButtonsTypes}
          communicationChannel={communicationChannel}
          setCommunicationChannel={setCommunicationChannel}
        />

        <ProtocolCategorySimple
          protocolCategory={protocolCategory}
          setProtocolCategory={setProtocolCategory}
          ticketReason={ticketReason}
          setTicketReason={setTicketReason}
          protocolClassification={protocolClassification}
          setProtocolClassification={setProtocolClassification}
          isEditing={isEditing}
        />

        <Row className='flex-1 flex-md-column'>
          <BrandSimple
            brandSelected={brandSelected}
            setBrandSelected={setBrandSelected}
            setNameBrandSelected={setNameBrandSelected}
          />

          <BU
            buSelected={buSelected}
            setBuSelected={setBuSelected}
          />
        </Row>

        <AdditionalContact
          contactName={contactName}
          setContactName={setContactName}
          contactMailAddress={contactMailAddress}
          setContactMailAddress={setContactMailAddress}
          contactPhoneNumber={contactPhoneNumber}
          setContactPhoneNumber={setContactPhoneNumber}
        />

        <OrderData
          order={order}
          setOrder={setOrder}
          invoice={invoice}
          setInvoice={setInvoice}
          manualDevolutionInvoiceNumber={manualDevolutionInvoiceNumber}
          setManualDevolutionInvoiceNumber={setManualDevolutionInvoiceNumber}
          protocolCategory={protocolCategory}
          ticketReason={ticketReason}
        />

        <DescriptionSimple
          description={description}
          setDescription={setDescription}
          protocolCategory={protocolCategory}
        />

        <TechnicalReport
          protocolCategory={protocolCategory}
          occurrenceDate={occurrenceDate}
          setOccurrenceDate={setOccurrenceDate}
          sampleType={sampleType}
          setSampleType={setSampleType}
          uncontaminatedProductSampleType={uncontaminatedProductSampleType}
          setUncontaminatedProductSampleType={setUncontaminatedProductSampleType}
          remainingBalance={remainingBalance}
          setRemainingBalance={setRemainingBalance}
          qtdRemainingBalance={qtdRemainingBalance}
          setQtdRemainingBalance={setQtdRemainingBalance}
          technicalDescription={technicalDescription}
          setTechnicalDescription={setTechnicalDescription}
          othersOccurrence={othersOccurrence}
          setOthersOccurrence={setOthersOccurrence}
          occurrenceType={occurrenceType}
          setOccurrenceType={setOccurrenceType}
          isEditing={isEditing}
        />

        <AttachmentSimple
          files={files}
          setFiles={setFiles}
        />

        <TagSimple
          tags={tags}
          setTags={setTags}
        />

        {fail && (
          <Text size={14} className="color-danger animated fadeIn mt-3">
            Favor revisar o preenchimento do formulário
          </Text>
        )}

        {errorMessage && (
          <Text size={14} className="color-danger animated fadeIn mt-3">
            {errorMessage}
          </Text>
        )}

        <Row className="mt-3 bg-gray flex-1 justify-center align-center">
          <Row className="py-4">
            <Button
              isRequesting={loading}
              form="open_protocol_form_simple"
              className="bg-primary"
              width="auto"
              title='button_submit_simple_form'
            >
              <Text className="color-white font-bold px-3">
                {isEditing ? 'Salvar Protocolo' : 'Registrar Protocolo'}
              </Text>
            </Button>
          </Row>
        </Row>
      </Column>
    </Form>
  );
};